import type { CSSProperties, ReactNode, ThHTMLAttributes } from 'react';
import { nextSortState, type SortState } from '../lib/sort.js';

export interface SortableThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortKey: string;
  sort: SortState | null;
  onSortChange: (next: SortState | null) => void;
  children: ReactNode;
  /** If false, the header renders as a plain non-clickable th. */
  sortable?: boolean;
}

export function SortableTh({
  sortKey,
  sort,
  onSortChange,
  children,
  sortable = true,
  style,
  ...thProps
}: SortableThProps) {
  if (!sortable) {
    return <th {...thProps} style={style}>{children}</th>;
  }

  const active = sort?.key === sortKey;
  const dir = active ? sort.dir : null;

  // Visual indicator: ▲ asc, ▼ desc, faint ⇅ when neutral (only on hover).
  const indicator = dir === 'asc' ? ' ▲' : dir === 'desc' ? ' ▼' : '';

  const baseStyle: CSSProperties = {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    color: active ? 'var(--text-primary)' : undefined,
    ...style,
  };

  return (
    <th
      {...thProps}
      style={baseStyle}
      onClick={(e) => {
        thProps.onClick?.(e);
        onSortChange(nextSortState(sort, sortKey));
      }}
      // CTSK-466 — keyboard accessibility (AC 1): sortable headers are
      // focusable and cycle the sort on Enter/Space, mirroring the
      // click path. Additive-only; API and mouse behavior unchanged.
      tabIndex={thProps.tabIndex ?? 0}
      onKeyDown={(e) => {
        thProps.onKeyDown?.(e);
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSortChange(nextSortState(sort, sortKey));
        }
      }}
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
      title={`Sort by ${typeof children === 'string' ? children : sortKey}`}
    >
      {children}
      <span aria-hidden="true" style={{ color: active ? 'var(--accent-primary)' : 'var(--text-muted)', fontSize: 10 }}>
        {indicator || ' ⇅'}
      </span>
    </th>
  );
}
